import { Text, Badge, Box } from '@chakra-ui/react';
import DataTableCard from './DataTableCard';
import { useBinderCardColors } from '../hooks/useBinderCardColors';
import { formatSimpleValue } from '../utils/fieldFormatting';
import ValidationBadge from './ValidationBadge';

interface LeaseVehicleCardProps {
  data?: any;
}

const LeaseVehicleCard = ({ data }: LeaseVehicleCardProps) => {
  const colors = useBinderCardColors();

  // Get processed vehicles data
  const vehicles = data?.vehicles || [];

  // Format lessor information from object
  const formatLessorInfo = (lessorObj: any) => {
    if (!lessorObj || typeof lessorObj !== 'object') return '';

    const name = lessorObj.name || '';
    const address = lessorObj.address || '';
    const city = lessorObj.city || '';
    const province = lessorObj.province_code || '';
    const postalCode = lessorObj.postal_code || '';

    const fullAddress = [address, city, province, postalCode]
      .filter(Boolean)
      .join(', ');

    return name && fullAddress
      ? `${name}\n${fullAddress}`
      : name || fullAddress || '';
  };

  const formatLeaseStatus = (isLeased: boolean) => {
    return (
      <Badge colorScheme="blue" variant="outline" fontSize="xs">
        {isLeased ? 'Yes' : 'No'}
      </Badge>
    );
  };

  // Create table rows from processed vehicle data
  const vehicleRows = vehicles.map((vehicle: any) => {
    const lessorInfo = formatLessorInfo(vehicle.lessor);

    return [
      vehicle.name,
      formatLeaseStatus(vehicle.isLeased),
      <Text
        color={
          lessorInfo && lessorInfo !== 'Not specified'
            ? colors.valueColor
            : 'gray.400'
        }
        fontStyle={
          !lessorInfo || lessorInfo === 'Not specified' ? 'italic' : 'normal'
        }
        whiteSpace="pre-line"
        fontSize="sm"
      >
        {formatSimpleValue(lessorInfo)}
      </Text>,
      <ValidationBadge status={vehicle.isValid ? 'pass' : 'caution'} />,
    ];
  });

  const headers = ['Vehicle', 'Leased?', 'Lessor', 'Outcome'];

  // Handle empty state
  if (vehicleRows.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No vehicle data available
        </Text>
      </Box>
    );
  }

  return (
    <DataTableCard
      description="Check to see that each leased vehicle has a lessor defined."
      headers={headers}
      rows={vehicleRows}
    />
  );
};

export default LeaseVehicleCard;
